import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { ReporteService, type BoletaPagoData } from '../services/reporteService';

interface Props {
  open: boolean;
  onClose: () => void;
  idPlanilla: number;
  idTrabajador: number;
}

const fmt = (n: number) => n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BoletaPagoDialog: React.FC<Props> = ({ open, onClose, idPlanilla, idTrabajador }) => {
  const [boleta, setBoleta] = useState<BoletaPagoData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && idPlanilla && idTrabajador) {
      loadBoleta();
    }
    return () => {
      setBoleta(null);
      setError(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idPlanilla, idTrabajador]);

  const loadBoleta = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ReporteService.getBoletaPago(idPlanilla, idTrabajador);
      setBoleta(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar boleta');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
      <head>
        <title>Boleta de Pago - ${boleta?.nombreTrabajador}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 8px 0; }
          th, td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          td.right { text-align: right; }
          .header { text-align: center; margin-bottom: 16px; }
          .section-title { font-weight: bold; margin: 12px 0 4px; background: #e3f2fd; padding: 4px 8px; }
          .totals { font-weight: bold; font-size: 14px; margin-top: 12px; }
          .neto { font-size: 16px; color: #1565c0; }
          @media print { body { margin: 10mm; } }
        </style>
      </head>
      <body>${printRef.current.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Boleta de Pago</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {boleta && (
          <Box ref={printRef}>
            {/* Cabecera */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">{boleta.empresa}</Typography>
              <Typography variant="caption">RUC: {boleta.rucEmpresa}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>BOLETA DE PAGO</Typography>
              <Typography variant="body2">Periodo: {boleta.periodo}</Typography>
            </Box>

            <Divider />

            {/* Datos del trabajador */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, my: 2 }}>
              <Typography variant="body2"><b>Trabajador:</b> {boleta.nombreTrabajador}</Typography>
              <Typography variant="body2"><b>Documento:</b> {boleta.documento}</Typography>
              <Typography variant="body2"><b>Area:</b> {boleta.area}</Typography>
              <Typography variant="body2"><b>F. Ingreso:</b> {boleta.fechaIngreso}</Typography>
              <Typography variant="body2"><b>Reg. Pensionario:</b> {boleta.regimenPensionario}</Typography>
              <Typography variant="body2"><b>CUSSP:</b> {boleta.cussp || '-'}</Typography>
              <Typography variant="body2"><b>Días laborados:</b> {boleta.diasLaborados}</Typography>
              <Typography variant="body2">
                <b>Días no laborados:</b> {boleta.diasNoLaborados > 0 ? boleta.diasNoLaborados : '-'}
              </Typography>
              <Typography variant="body2">
                <b>Días desc. médico:</b> {boleta.diasSubsidiados > 0 ? boleta.diasSubsidiados : '-'}
              </Typography>
              <Typography variant="body2">
                <b>Horas extras:</b> {boleta.horasExtras25 > 0 ? `25%: ${boleta.horasExtras25}h ` : ''}
                {boleta.horasExtras35 > 0 ? `35%: ${boleta.horasExtras35}h ` : ''}
                {boleta.horasExtras100 > 0 ? `100%: ${boleta.horasExtras100}h` : ''}
                {boleta.horasExtras25 === 0 && boleta.horasExtras35 === 0 && boleta.horasExtras100 === 0 ? '-' : ''}
              </Typography>
            </Box>

            <Divider />

            {/* Remuneraciones */}
            {boleta.remuneraciones.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, bgcolor: '#e8f5e9', px: 1, py: 0.5 }}>
                  REMUNERACIONES
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cod.</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {boleta.remuneraciones.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.codigo}</TableCell>
                        <TableCell>{c.descripcion}</TableCell>
                        <TableCell align="right">S/ {fmt(c.monto)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2}><b>Total Remuneraciones</b></TableCell>
                      <TableCell align="right"><b>S/ {fmt(boleta.totalRemuneraciones)}</b></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </>
            )}

            {/* Descuentos */}
            {boleta.descuentos.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, bgcolor: '#ffebee', px: 1, py: 0.5 }}>
                  DESCUENTOS
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cod.</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {boleta.descuentos.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.codigo}</TableCell>
                        <TableCell>{c.descripcion}</TableCell>
                        <TableCell align="right">S/ {fmt(c.monto)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2}><b>Total Descuentos</b></TableCell>
                      <TableCell align="right"><b>S/ {fmt(boleta.totalDescuentos)}</b></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </>
            )}

            {/* Aportes del empleador */}
            {boleta.aportes.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, bgcolor: '#fff3e0', px: 1, py: 0.5 }}>
                  APORTES DEL EMPLEADOR
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cod.</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {boleta.aportes.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.codigo}</TableCell>
                        <TableCell>{c.descripcion}</TableCell>
                        <TableCell align="right">S/ {fmt(c.monto)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2}><b>Total Aportes</b></TableCell>
                      <TableCell align="right"><b>S/ {fmt(boleta.totalAportes)}</b></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </>
            )}

            {/* Neto a pagar */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                NETO A PAGAR: S/ {fmt(boleta.netoPagar)}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!boleta}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoletaPagoDialog;
